import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../../../utils/config';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ReportFilters = ({ onFiltersChange, reportKey, className = "" }) => {
  const [filterOptions, setFilterOptions] = useState({});
  const [savedFilters, setSavedFilters] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [selectedFilters, setSelectedFilters] = useState({
    dateRange: 'last-30-days',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    if (reportKey) {
      fetchFilterOptions();
      fetchSavedFilters();
    }
  }, [reportKey]);

  const fetchFilterOptions = async () => {
    if (!reportKey) return;
    
    setIsLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const headers = {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      const response = await axios.get(API_ENDPOINTS.REPORTS_FILTERS_OPTIONS, {
        headers,
        params: { report_key: reportKey }
      });

      // Handle standardized response format: { status: "ok", data: {...} }
      // or direct object format
      let data = {};
      if (response.data?.status === 'ok' && response.data?.data) {
        data = response.data.data;
      } else {
        data = response.data?.data || response.data || {};
      }
      setFilterOptions(data);
    } catch (err) {
      const errorMsg = err?.response?.data?.message || 
                     err?.response?.data?.detail || 
                     err?.message || 
                     'Failed to fetch filter options';
      setError(errorMsg);
      console.error('Error fetching filter options:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSavedFilters = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      const params = reportKey ? { report_key: reportKey } : {};
      const response = await axios.get(API_ENDPOINTS.REPORTS_FILTERS_LIST, {
        headers,
        params
      });

      // Handle standardized response format: { status: "ok", data: [...] }
      // or direct array/object format
      let data = [];
      if (response.data?.status === 'ok' && response.data?.data) {
        data = Array.isArray(response.data.data) ? response.data.data : [];
      } else if (Array.isArray(response.data)) {
        data = response.data;
      } else {
        data = response.data?.results || response.data?.data || [];
      }
      
      setSavedFilters(data);
    } catch (err) {
      console.error('Error fetching saved filters:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveFilter = async () => {
    const name = prompt('Enter a name for this filter preset:');
    if (!name) return;

    try {
      const token = localStorage.getItem('token');
      const headers = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      await axios.post(
        API_ENDPOINTS.REPORTS_FILTERS_CREATE,
        {
          report_key: reportKey,
          name,
          params: selectedFilters
        },
        { headers }
      );

      fetchSavedFilters();
    } catch (err) {
      const errorMsg = err?.response?.data?.message || 
                     err?.response?.data?.detail || 
                     err?.message || 
                     'Failed to save filter';
      alert(errorMsg);
      console.error('Error saving filter:', err);
    }
  };

  const handleDeleteFilter = async (filterId) => {
    if (!confirm('Are you sure you want to delete this saved filter?')) return;

    try {
      const token = localStorage.getItem('token');
      const headers = {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      await axios.delete(API_ENDPOINTS.REPORTS_FILTERS_DELETE(filterId), { headers });
      fetchSavedFilters();
    } catch (err) {
      const errorMsg = err?.response?.data?.message || 
                     err?.response?.data?.detail || 
                     err?.message || 
                     'Failed to delete filter';
      alert(errorMsg);
      console.error('Error deleting filter:', err);
    }
  };

  const handleLoadFilter = (savedFilter) => {
    setSelectedFilters(savedFilter.params || {});
    if (onFiltersChange) {
      onFiltersChange(savedFilter.params || {});
    }
  };

  const handleApplyFilters = () => {
    // Convert date range preset to actual dates if needed
    let filters = { ...selectedFilters };
    
    if (filters.dateRange && filters.dateRange !== 'custom') {
      const today = new Date();
      let startDate = new Date();
      
      switch (filters.dateRange) {
        case 'today':
          startDate = new Date(today);
          break;
        case 'last-7-days':
          startDate.setDate(today.getDate() - 7);
          break;
        case 'last-30-days':
          startDate.setDate(today.getDate() - 30);
          break;
        case 'last-90-days':
          startDate.setDate(today.getDate() - 90);
          break;
        case 'this_month':
          startDate = new Date(today.getFullYear(), today.getMonth(), 1);
          break;
        case 'last_month':
          startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
          filters.end_date = lastMonthEnd.toISOString().split('T')[0];
          break;
        case 'ytd':
          startDate = new Date(today.getFullYear(), 0, 1);
          break;
        default:
          break;
      }
      
      if (filters.dateRange !== 'last_month') {
        filters.end_date = today.toISOString().split('T')[0];
      }
      filters.start_date = startDate.toISOString().split('T')[0];
    }

    // Remove dateRange from final filters as it's just a preset
    const { dateRange, ...finalFilters } = filters;
    
    if (onFiltersChange) {
      onFiltersChange(finalFilters);
    }
  };

  const handleResetFilters = () => {
    const resetFilters = {
      dateRange: 'last-30-days',
      start_date: '',
      end_date: ''
    };
    setSelectedFilters(resetFilters);
    if (onFiltersChange) {
      onFiltersChange({});
    }
  };

  const dateRangeOptions = [
    { value: 'today', label: 'Today' },
    { value: 'last-7-days', label: 'Last 7 Days' },
    { value: 'last-30-days', label: 'Last 30 Days' },
    { value: 'last-90-days', label: 'Last 90 Days' },
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'ytd', label: 'Year to Date' },
    { value: 'custom', label: 'Custom Range' }
  ];

  return (
    <div className={`bg-card rounded-lg border border-border p-6 ${className}`}>
      <div className="flex items-center space-x-2 mb-6">
        <Icon name="Filter" size={20} color="var(--color-primary)" />
        <h3 className="text-lg font-semibold text-foreground">Report Filters</h3>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
          <div className="flex items-center space-x-2 text-destructive text-sm">
            <Icon name="AlertCircle" size={16} />
            <span>{error}</span>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {/* Date Range Preset */}
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">Date Range</label>
          <select
            value={selectedFilters.dateRange || 'last-30-days'}
            onChange={(e) => setSelectedFilters(prev => ({ ...prev, dateRange: e.target.value }))}
            className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {dateRangeOptions.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        {/* Custom Date Range */}
        {selectedFilters.dateRange === 'custom' && (
          <>
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Start Date</label>
              <input
                type="date"
                value={selectedFilters.start_date || ''}
                onChange={(e) => setSelectedFilters(prev => ({ ...prev, start_date: e.target.value }))}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">End Date</label>
              <input
                type="date"
                value={selectedFilters.end_date || ''}
                onChange={(e) => setSelectedFilters(prev => ({ ...prev, end_date: e.target.value }))}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </>
        )}

        {/* Dynamic Filter Options from API */}
        {filterOptions && Object.keys(filterOptions).map(key => {
          const options = filterOptions[key];
          if (!Array.isArray(options) || options.length === 0) return null;

          return (
            <div key={key}>
              <label className="text-sm font-medium text-foreground mb-2 block">
                {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </label>
              <select
                value={selectedFilters[key] || ''}
                onChange={(e) => setSelectedFilters(prev => ({ ...prev, [key]: e.target.value }))}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">All</option>
                {options.map(option => {
                  const value = typeof option === 'object' ? option.value || option.id : option;
                  const label = typeof option === 'object' ? option.label || option.name || value : option;
                  return (
                    <option key={value} value={value}>{label}</option>
                  );
                })}
              </select>
            </div>
          );
        })}
      </div>

      <div className="flex flex-col space-y-3 mt-6">
        <Button
          variant="default"
          onClick={handleApplyFilters}
          iconName="Search"
          iconPosition="left"
          fullWidth
        >
          Apply Filters
        </Button>
        
        <Button
          variant="outline"
          onClick={handleResetFilters}
          iconName="RotateCcw"
          iconPosition="left"
          fullWidth
        >
          Reset Filters
        </Button>

        {reportKey && (
          <Button
            variant="ghost"
            onClick={handleSaveFilter}
            iconName="Save"
            iconPosition="left"
            fullWidth
            size="sm"
          >
            Save Filter Preset
          </Button>
        )}
      </div>

      {/* Saved Filters */}
      {savedFilters.length > 0 && (
        <div className="mt-6 pt-6 border-t border-border">
          <h4 className="text-sm font-medium text-foreground mb-3">Saved Presets</h4>
          <div className="space-y-2">
            {savedFilters.map((filter) => (
              <div
                key={filter.id}
                className="flex items-center justify-between p-2 hover:bg-muted rounded-md group"
          >
                <button
                  onClick={() => handleLoadFilter(filter)}
                  className="flex-1 text-left text-xs text-muted-foreground hover:text-foreground"
                >
                  {filter.name}
                </button>
                <button
                  onClick={() => handleDeleteFilter(filter.id)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Icon name="Trash2" size={14} className="text-destructive" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportFilters;
